<template>
  <RfAuth
    :title="getTitle"
    :error="error"
    :success="success"
    @submit="onLoginWrapper"
    @keyup.enter="onLoginWrapper"
  >
    <template #inputs>
      <RfInput
        v-model="email"
        class="w-full"
        size="large"
        title="Email"
        variant="outlined"
        placeholder="Enter your email"
        type="email"
        name="email"
        autocomplete="email"
        @input="checkInternalEmail"
      >
        <template #prepend> <RfEnvelopeIcon class="h-6! w-6!" /> </template>
      </RfInput>
      <!-- Hide password field for internal users with Okta enabled -->
      <RfInput
        v-if="displayContainer === null && !shouldShowOktaUI"
        v-model="password"
        class="mt-4 w-full"
        size="large"
        title="Password"
        variant="outlined"
        :type="showPassword ? 'text' : 'password'"
        placeholder="Enter your password"
        name="password"
        autocomplete="current-password"
      >
        <template #prepend> <RfLockKeyholeIcon class="h-6! w-6! shrink-0" /> </template>
        <template #append>
          <button type="button" class="h-6! w-6! shrink-0" @click="showPassword = !showPassword">
            <RfEyeOpenIcon v-if="showPassword" class="h-6! w-6!" />
            <RfEyeClosedIcon v-else class="h-6! w-6!" />
          </button>
        </template>
      </RfInput>
    </template>

    <template #footer>
      <div class="flex grow flex-col gap-4">
        <!-- Hide Login button when Okta UI is shown -->
        <RfButtonNew
          v-if="[null, 'sso'].includes(displayContainer) && !shouldShowOktaUI"
          class="w-full"
          size="x-large"
          :loading="loading"
          :disabled="loading"
          nativeType="submit"
          @click="resetValues"
        >
          <RfLoginIcon class="h-6! w-6!" />
          Login
        </RfButtonNew>

        <template v-if="displayContainer === null">
          <!-- Okta login button for internal users when Okta is enabled -->
          <RfButtonNew
            v-if="shouldShowOktaUI"
            class="w-full"
            size="x-large"
            :loading="loading"
            :disabled="loading"
            nativeType="submit"
          >
            <RfKeyIcon class="h-6! w-6!" />
            Sign In with Okta
          </RfButtonNew>

          <!-- Regular login options for non-internal users or when Okta is disabled -->
          <template v-else>
            <RfButtonNew
              type="plain"
              size="link"
              :loading="loading"
              :disabled="loading"
              @click="displayContainer = 'sso'"
            >
              <RfKeyIcon class="h-6! w-6!" />
              Login with Your Organization
            </RfButtonNew>
            <RfButtonNew
              type="plain"
              size="link-sm"
              :loading="loading"
              :disabled="loading"
              @click="displayContainer = 'forgot-password'"
            >
              Forgot Password
            </RfButtonNew>
          </template>
        </template>
        <RfButtonNew
          v-if="displayContainer === 'forgot-password'"
          class="w-full"
          size="x-large"
          type="secondary"
          :loading="loading"
          :disabled="loading"
          nativeType="submit"
          @click="resetValues"
        >
          <RfPaperPlaneIcon class="h-6! w-6!" />
          Send Reset Email
        </RfButtonNew>
        <RfButtonNew
          v-if="['sso', 'forgot-password'].includes(displayContainer)"
          type="plain"
          size="link-sm"
          :loading="loading"
          :disabled="loading"
          @click="displayContainer = null"
        >
          Back to Login
        </RfButtonNew>
      </div>
    </template>
  </RfAuth>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ApiUsers from "@/apis/ApiUsers";
import RfAuth from "@/blocks/RfAuth.vue";
import { useUserStore } from "@/pinia/userStore";
import { parseJsonSilently } from "@/utils/JsonUtls";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfLoginIcon from "@/components/icons/RfLoginIcon.vue";
import RfKeyIcon from "@/components/icons/RfKeyIcon.vue";
import RfPaperPlaneIcon from "@/components/icons/RfPaperPlaneIcon.vue";
import RfInput from "@/components/inputs/RfInput.vue";
import RfEnvelopeIcon from "@/components/icons/RfEnvelopeIcon.vue";
import RfLockKeyholeIcon from "@/components/icons/RfLockKeyholeIcon.vue";
import RfEyeOpenIcon from "@/components/icons/RfEyeOpenIcon.vue";
import RfEyeClosedIcon from "@/components/icons/RfEyeClosedIcon.vue";

const userStore = useUserStore();
const route = useRoute();
const router = useRouter();

const email = ref(null);
const password = ref(null);
const displayContainer = ref(null);
const success = ref(null);
const error = ref(null);
const showPassword = ref(false);
const loading = ref(false);
const isInternalEmail = ref(false);
const oktaEnabled = ref(false);

const getTitle = computed(() => {
  if (displayContainer.value === "forgot-password") return "Reset Password";
  if (displayContainer.value === "sso") return "SSO Login";
  return "";
});

const shouldShowOktaUI = computed(() => {
  return oktaEnabled.value && isInternalEmail.value;
});

const resetValues = () => {
  success.value = null;
  error.value = null;
};

const fetchAuthConfig = async () => {
  try {
    loading.value = true;
    const configAuth = await ApiUsers.getConfig();
    oktaEnabled.value = configAuth.okta_enabled;
  } catch (_e) {
    error.value = "Failed to fetch auth config";
    oktaEnabled.value = false;
  } finally {
    loading.value = false;
  }
};

const checkInternalEmail = () => {
  if (["forgot-password", "sso"].includes(displayContainer.value)) {
    isInternalEmail.value = false;
    return;
  }
  // Check if email ends with @recurly.com or @redfast.com
  const internalDomains = ["@recurly.com", "@redfast.com"];
  isInternalEmail.value =
    email.value && internalDomains.some(domain => email.value.toLowerCase().endsWith(domain));
};

const redirectToOkta = () => {
  // Redirect to Okta OAuth flow
  const phaserHost = import.meta.env.VITE_ENDPOINT || "https://localhost:3001/";
  window.location.href = `${phaserHost}users/auth/okta`;
};

const onLogin = async () => {
  try {
    // Prevent internal users from using password login when Okta is enabled
    if (shouldShowOktaUI.value) {
      redirectToOkta();
      return;
    }
    resetValues();
    loading.value = true;
    await userStore.login({ email: email.value, password: password.value });
  } catch (e) {
    error.value = e.message;
    loading.value = false;
  }
};

const onForgotPassword = async () => {
  try {
    if (!email.value) throw new Error("Email must not be empty.");

    // Check if internal email with Okta enabled - they can't reset password
    const internalDomains = ["@recurly.com", "@redfast.com"];
    const isInternal = internalDomains.some(domain => email.value.toLowerCase().endsWith(domain));
    if (oktaEnabled.value && isInternal) {
      throw new Error(
        "Internal employees must sign in with Okta. Password reset is not available.",
      );
    }

    resetValues();
    loading.value = true;
    await ApiUsers.sendResetLoginUser(email.value);
    success.value =
      "If an account with this email exists, you'll receive password reset instructions shortly.";
  } catch (e) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
};

const onSSO = async () => {
  try {
    if (!email.value) throw new Error("Email must not be empty.");
    loading.value = true;
    const { company_id } = await ApiUsers.getEmailCompany(encodeURI(email.value));
    const { sso_authorization_url } = await ApiUsers.loginSSO(company_id);
    window.location.href = sso_authorization_url;
  } catch (e) {
    error.value = e.message;
    loading.value = false;
  }
};

const onLoginWrapper = () => {
  if (displayContainer.value === "forgot-password") return onForgotPassword();
  if (displayContainer.value === "sso") return onSSO();
  if (shouldShowOktaUI.value) return redirectToOkta();
  return onLogin();
};

onMounted(async () => {
  // Fetch auth configuration to check if Okta is enabled
  await fetchAuthConfig();

  const { confirmation } = route.query;
  if (confirmation === "true") {
    success.value = "You have confirmed your email. Please login.";
  } else if (confirmation === "false") {
    success.value = "Your email has already been confirmed. Please login.";
  }
});

watch(displayContainer, () => resetValues());
watch(
  () => !displayContainer.value && shouldShowOktaUI.value,
  to => (error.value = to ? "Internal employees must sign in with Okta" : null),
);

watch(
  () => userStore.currUser,
  to => {
    if (!to) return;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("docs") === "1") {
      if (urlParams.get("redirect")) {
        const docsObj = new URL(to.docs);
        const baseUrl = new URL(`${docsObj.protocol}//${docsObj.hostname}`);
        const path = decodeURIComponent(urlParams.get("redirect"));
        const urlObj = new URL(path, baseUrl);
        urlObj.search = docsObj.search;

        window.location = urlObj.toString();
      } else {
        window.location = to.docs;
      }
    } else {
      const url = new URL(window.location.href);
      const redirect = url.searchParams.get("redirect");

      if (redirect) {
        const location = parseJsonSilently(redirect);
        if (location.pathname || location.hash || location.search) {
          const newUrl = new URL(location.pathname, window.location.origin);
          newUrl.hash = location.hash;
          newUrl.search = location.search;
          window.location = newUrl.toString();
          return;
        }
      }

      // Auto-redirect corp_it users to company access page
      if (to.role === "corp_it") {
        router.push({ name: "Company Access" });
        return;
      }

      window.location = "/";
    }
  },
);
</script>
